<template>
  <div v-if="!loading" class="profile-info-container">
    <div class="breadcrumb-navigation">
      <span class="navigation" @click="handleBreadCrumbNavigationClick">{{ $t(`admin.layout.newLayout.menu.AgencyApplicationList`) }}</span>
      <span class="split">/</span>
      <span class="navigation active">{{ $t(`admin.layout.newLayout.menu.AgencyApplicationProfile`) }}</span>
    </div>
    <div class="application-list-navigation">
      <el-button class="navigation-btn prev-btn" @click="handlePrevBtnClick">
        <i class="btn-icon el-icon-arrow-left"></i>
      </el-button>
      <span class="position">
        <span class="current">{{ (currentApplicationIdIndex + 1)|formatBigNumber }}</span>
        <span class="split">/</span>
        <span class="total">{{ totalCount|formatBigNumber }}</span>
      </span>
      <el-button class="navigation-btn next-btn" @click="handleNextBtnClick">
        <i class="btn-icon el-icon-arrow-right"></i>
      </el-button>
      <RectLoading v-show="applicationIdListLoading" class="loading" ref="loading"></RectLoading>
    </div>
    <div class="base-info-container" :class="(candidateInfo.is_employee || candidateInfo.is_alumni) && 'extra'">
      <BaseInfo
        hiddenCandidateStatus
        isAgency
        :candidateInfo="candidateInfo"
        :statusGroupList="statusGroupList"
        @toRejectStatus="handleToRejectStatus"
        @changeStatus="handleChangeStatus"
      />
    </div>
    <div class="detail-info-container" ref="detailInfoContainer">
      <TabCard
        class="info-tab-card"
        :activeTab="activeTab"
        :tabList="tabList"
        @change="onTabChange"
      >
        <component
          v-bind="activeTab.props"
          v-on="activeTab.events"
          isAgency
          :is="activeTab.component"
          :jobPremiumList="jobPremiumList"
          :candidateBaseInfo="candidateInfo"
          :hiddenVideoQuestion="!hasVideoQuestionPremium"
          :hiddenAttachmentQuestion="!hasAttachmentQuestionPremium"
          :hiddenQuestionTemplate="true"
        >
        </component>
        <template #assessmentScore>
          <div class="score-container">
            <el-tooltip effect="light" popper-class="light-tooltip-popper" :content="$t(`${i18nPrefix}.label.resumeFit`)">
              <div ref="assessmentTotalFit" class="chart-score total-fit"></div>
            </el-tooltip>
            <el-tooltip effect="light" popper-class="light-tooltip-popper" :content="$t(`${i18nPrefix}.label.problemSolvingFit`)">
              <div ref="assessmentMentalFit" class="chart-score mental-fit"></div>
            </el-tooltip>
          </div>
        </template>
      </TabCard>
    </div>
    <div class="fixed-list-navigation" :style="fixedNavigationStyle">
      <el-button class="navigation-btn prev-btn" :disabled="currentApplicationIdIndex === 0" @click="handlePrevBtnClick">
        <i class="btn-icon iconfont iconArrows"></i>
      </el-button>
      <el-button class="navigation-btn next-btn" :disabled="(currentApplicationIdIndex + 1) === totalCount" @click="handleNextBtnClick">
        <i class="btn-icon iconfont iconArrows"></i>
      </el-button>
    </div>
  </div>
</template>

<script>
import _ from 'lodash'
import * as echarts from 'echarts'
import { mapActions } from 'vuex'
import { addI18nProperty, createI18nProperty } from '@/i18n'
import { generateFitEchartsOption } from '@/constant/application'
import BaseInfo from '../../../adminNew/candidate/profile/components/baseInfo.vue'
import TabCard from '../../../adminNew/candidate/profile/components/tabCard.vue'
import Resume from '../../../adminNew/candidate/profile/resume.vue'
import ActivityTracker from '../../../adminNew/candidate/profile/activityTracker.vue'
import OpenQuestion from '../../../adminNew/candidate/profile/openQuestion.vue'
import Assessment from '../../../adminNew/candidate/profile/assessment.vue'
import RectLoading from '@/components/loading/rectLoading.vue'
import moment from 'moment'

const PAGE_TYPE = {
  RESUME: 'resume',
  OPEN_QUESTION: 'screening',
  ASSESSMENT: 'assessment',
  ACTIVITY_TRACKER: 'activity-tracker'
}

export default {
  components: {
    BaseInfo,
    TabCard,
    Resume,
    RectLoading
  },
  metaInfo () {
    return {
      title: this.pageTitle
    }
  },
  data () {
    return {
      i18nPrefix: 'admin.application.candidate',
      loading: false,
      activeTab: null,
      pageDataLoader: null,
      candidateInfo: null,
      statusGroupList: [],
      statusList: [],
      defaultStatusKey: null,
      applicationIdList: [],
      applicationIdListLoading: false,
      fixedNavigationRightResetter: null,
      fixedNavigationRight: 110,
      jobPremiumList: [],
      hasVideoQuestionPremium: false,
      hasAttachmentQuestionPremium: false,
      hasAssessmentPremium: false
    }
  },
  computed: {
    fixedNavigationStyle () {
      return {
        right: `${this.fixedNavigationRight}px`
      }
    },
    tabList () {
      const tabList = [
        {
          pageType: PAGE_TYPE.RESUME,
          component: Resume,
          suffix: 'resumeScore'
        },
        {
          pageType: PAGE_TYPE.OPEN_QUESTION,
          component: OpenQuestion,
          suffix: 'ratingScore'
        },
        {
          pageType: PAGE_TYPE.ASSESSMENT,
          component: Assessment,
          suffix: 'assessmentScore',
          hidden: !this.hasAssessmentPremium
        },
        {
          pageType: PAGE_TYPE.ACTIVITY_TRACKER,
          component: ActivityTracker
        }
      ]
      const hidePageType = []
      if (this.candidateInfo?.is_agency_uploaded_and_hid) {
        hidePageType.push(PAGE_TYPE.TAG)
      }
      return _.chain(tabList).filter(tab => {
        if (hidePageType.includes(tab.pageType)) {
          return false
        }
        if (tab.permission) {
          return this.hasAnyPermission(tab.permission)
        }

        if (tab.hidden) {
          return false
        }
        return true
      }).map(tab => {
        const pageTypeKey = _.findKey(PAGE_TYPE, pageType => pageType === tab.pageType)
        tab.labelI18nKey = `${this.i18nPrefix}.tabMenu.${pageTypeKey}`
        return tab
      }).value()
    },
    pageType () {
      return this.$route.params.pageType
    },
    candidateId () {
      return this.$route.query.candidate_id
    },
    jobId () {
      return this.$route.query.job_id
    },
    listPageParams () {
      return this.$route.query.list_params
    },
    pageTitle () {
      const title = this.$t(`${this.i18nPrefix}.title.detailResume`)
      const tabLabel = this.activeTab && this.$t(this.activeTab.labelI18nKey)
      const candidateName = this.candidateInfo?.other_name
      return _.chain([candidateName, title, tabLabel]).filter().join(' - ').value()
    },
    totalCount () {
      return this.applicationIdList.length
    },
    currentApplicationIdIndex () {
      return _.findIndex(this.applicationIdList, { id: this.candidateInfo.application_id })
    }
  },
  created () {
    this.refreshActiveTab()
    this.pageDataLoader = this.initPageData()
    this.loadApplicationIdList()
    this.loadJobInfo()
  },
  mounted () {
    this.pageDataLoader.then(() => {
      this.initTabScore()
      this.$nextTick(() => {
        this.resetFixedNavigationRight()
      })
    })
    this.fixedNavigationRightResetter = _.debounce(this.resetFixedNavigationRight, 10)
    window.addEventListener('resize', this.fixedNavigationRightResetter)
  },
  destroyed () {
    window.removeEventListener('resize', this.fixedNavigationRightResetter)
  },
  methods: {
    ...mapActions('application', ['getApplicationCandidateBaseInfo']),
    ...mapActions('agency', ['getApplicationIdList', 'getAgencyJobInfo']),
    resetFixedNavigationRight () {
      const rect = this.$refs.detailInfoContainer.getBoundingClientRect()
      const right = window.innerWidth - rect.left - rect.width + 68
      this.fixedNavigationRight = _.max([right, 104])
    },
    refreshActiveTab () {
      if (!this.pageType) {
        const pageType = this.tabList[0].pageType
        this.$router.push({ path: `/v-admin/agency/application/profile/${pageType}`, query: this.$route.query })
        return
      }
      if (this.activeTab && this.activeTab.pageType === this.pageType) {
        return
      }
      const tab = _.find(this.tabList, { pageType: this.pageType })
      if (!tab) {
        const pageType = this.tabList[0].pageType
        this.$router.replace({ path: `/v-admin/agency/application/profile/${pageType}`, query: this.$route.query })
      } else if (tab.permission && !this.hasAnyPermission(tab.permission)) {
        this.$router.replace('/error/no-permission')
      } else {
        this.activeTab = tab
        this.initTabScore()
      }
    },
    async loadJobInfo () {
      this.jobLoading = true
      try {
        const resp = await this.getAgencyJobInfo({ jobId: this.jobId })
        if (resp) {
          const job = resp
          addI18nProperty(job, 'jobName', 'cn_name', 'name')
          this.jobName = job.jobName
          this.hasProblemSolvingRequired = resp.problem_solving_required === '1'
          this.hasAssessmentValueRequired = resp.assessment_value_required === '1'
          this.hasPersonalityRequired = resp.personality_required === '1'
          this.isElcOriginCompany = resp.is_elc_origin_company
          this.hasAssessmentPremium = resp.has_assessment_premium
          this.hasResumeMatchPremium = resp.has_resume_match_premium
          this.hasVideoQuestionPremium = resp.has_video_question_premium
          this.hasAttachmentQuestionPremium = resp.has_attachment_question_premium
          this.job = job
        }
      } finally {
        this.jobLoading = false
      }
    },
    async loadCandidateProfileInfo () {
      const params = {
        candidate_id: this.candidateId,
        job_id: this.jobId
      }
      const resp = await this.getApplicationCandidateBaseInfo(params)
      if (resp) {
        const mobile = resp.mobile || resp.second_phone
        const countryCode = resp.country_code
        resp.mobileWithCountryCode = _.chain([countryCode, mobile]).filter().join(' ').value()
        addI18nProperty(resp.job, 'i18nName', 'cn_title', 'title')
        addI18nProperty(resp.company, 'i18nName', 'cn_name', 'name')
        const age = moment().diff(resp.birthday, 'year')
        createI18nProperty(resp, 'yearsOld', `${this.i18nPrefix}.label.yearsOld`, { age })
        const profile = resp
        profile.total_avg_interview_rate = this.total_avg_interview_rate
        profile.interview_rate_list = this.interview_rate_list
        profile.interview_questions = this.interview_questions
        profile.total_avg_rate = this.total_avg_rate
        profile.rate_list = this.rate_list
        this.candidateInfo = profile
      }
    },
    async loadApplicationIdList () {
      if (!this.listPageParams) {
        return
      }
      this.applicationIdListLoading = true
      const params = {
        job_id: this.jobId,
        list_params: this.listPageParams
      }
      try {
        const resp = await this.getApplicationIdList(params)
        if (resp) {
          this.applicationIdList = resp
        }
      } finally {
        this.applicationIdListLoading = false
      }
    },
    async initPageData () {
      this.$eventBus.$emit('showLoading', true)
      this.loading = true
      try {
        // this.setApplicationViewed()
        await Promise.all([this.loadCandidateProfileInfo()])
      } finally {
        this.loading = false
        this.$eventBus.$emit('showLoading', false)
      }
    },
    onTabChange (tab) {
      const pageType = tab.pageType
      const path = `/v-admin/agency/application/profile/${pageType}`
      if (this.$route.path === path) {
        return
      }
      this.$router.push({ path, query: this.$route.query })
    },
    async initTabScore () {
      if (!this.activeTab || !this.candidateInfo) {
        return
      }
      await this.$nextTick()
      // const fitness = this.candidateInfo.fitness
      const fitMap = {}
      const hideRefNames = []
      if (this.hasAssessmentPremium) {
        const hasFinishedPersonality = this.candidateInfo.personality_finished
        const hasFinishedProblemSolving = this.candidateInfo.problem_solving_finished
        if (hasFinishedPersonality) {
          fitMap.assessmentTotalFit = parseInt(this.candidateInfo.total_fit * 100) || 0
        } else {
          hideRefNames.push('assessmentTotalFit')
        }
        if (hasFinishedProblemSolving) {
          fitMap.assessmentMentalFit = parseInt(this.candidateInfo.mental_ability * 100) || 0
        } else {
          hideRefNames.push('assessmentMentalFit')
        }
      }
      _.forEach(fitMap, (val, refName) => {
        const refs = this.$refs[refName]
        if (!refs) {
          return
        }
        let el = null
        if (refs instanceof HTMLElement) {
          el = refs
        } else {
          el = refs[0]
        }
        let color = 'rgb(226, 118, 118)' // red
        if (val > 40 && val < 70) {
          color = 'rgb(244, 169, 88)' // orange
        } else if (val >= 70) {
          color = 'rgb(81, 191, 130)' // green
        }
        const option = generateFitEchartsOption(val, val, color, 14)
        const series = option.series
        series.axisLine.lineStyle.width = 2
        let chart = echarts.getInstanceByDom(el)
        if (chart) {
          chart.resize()
        } else {
          chart = echarts.init(el)
        }
        chart.setOption(option)
      })
      _.forEach(hideRefNames, async (refName) => {
        let refs = this.$refs[refName]
        if (!refs) {
          await this.$nextTick()
          refs = this.$refs[refName]
        }
        let el = null
        if (refs instanceof HTMLElement) {
          el = refs
        } else if (refs) {
          el = refs[0]
        }
        if (el) {
          el.style.display = 'none'
        }
      })
    },
    handleToRejectStatus () {
      this.defaultStatusKey = 'Disqualified'
      this.$refs.changeStatusDialog.showDialog()
    },
    handleChangeStatus (status) {
      this.defaultStatusKey = status.key
      this.$refs.changeStatusDialog.showDialog()
    },
    handleBreadCrumbNavigationClick () {
      const query = this.$route.query
      let url = `/v-agency/job/${query.job_id}/application`
      if (query.back) {
        url = query.back
      }
      this.$router.push(url)
    },
    handlePrevBtnClick () {
      if (this.applicationIdListLoading) {
        return
      }
      const info = this.applicationIdList[this.currentApplicationIdIndex - 1]
      if (!info) {
        return
      }
      const query = {
        ...this.$route.query,
        candidate_id: info.candidate_id,
        job_id: info.job_id
      }
      this.$router.push({ query })
      this.initPageData().then(() => {
        this.initTabScore()
      })
    },
    handleNextBtnClick () {
      if (this.applicationIdListLoading) {
        return
      }
      const info = this.applicationIdList[this.currentApplicationIdIndex + 1]
      if (!info) {
        return
      }
      const query = {
        ...this.$route.query,
        candidate_id: info.candidate_id,
        job_id: info.job_id
      }
      this.$router.push({ query })
      this.initPageData().then(() => {
        this.initTabScore()
      })
    }
  },
  watch: {
    pageType () {
      this.refreshActiveTab()
    },
    candidateId () {
      this.initPageData()
    }
  }
}
</script>

<style lang="scss" scoped>
.profile-info-container {
  display: flex;
  flex-direction: column;
  width: 91.5%;
  min-width: 1312px;
  margin: 50px auto 0;
  position: relative;

  .breadcrumb-navigation {
    line-height: 25px;
    font-size: 18px;
    color: #606266;
    position: absolute;
    top: -47px;
    left: 0;

    .split {
      margin: 0 10px;
    }

    .navigation {
      cursor: pointer;
      color: #0096cc;

      &.active {
        cursor: default;
        color: #606266;
      }
    }
  }

  .application-list-navigation {
    position: absolute;
    top: -50px;
    right: 0;

    .navigation-btn {
      padding: 0 8px;
      line-height: 28px;
      border-radius: 2px;
      border-color: #c8c8c8;

      .btn-icon {
        font-size: 12px;
      }
    }

    .position  {
      margin: 0 56px;
      line-height: 25px;
      font-size: 18;
      color: #606266;

      .split {
        margin: 0 10px;
      }
    }

    .loading {
      position: absolute;
      left: 0;
      top: 0;

      ::v-deep .spinner {
        height: 30px;
      }
    }
  }

  .base-info-container {
    padding: 30px 40px;
    border-radius: 6px;
    background: #fff;
  }

  .extra {
    padding-top: 35px;
  }

  .detail-info-container {
    flex: 1;
    margin-top: 20px;
    padding: 25px;
    border-radius: 6px;
    background: #fff;

    .info-tab-card {
      min-height: 100%;
      .score-container {
        position: absolute;
        right: -2px;
        top: 0;
        transform: translate(0, -50%);

        .score-item {
          display: inline-block;
          width: 30px;
          height: 30px;
          line-height: 26px;
          border: 2px solid #0096cc;
          border-radius: 50%;
          background-color: #fff;
          color: #0096cc;
          text-align: center;
          font-weight: bold;
          font-size: 14px;

          & + .score-item{
            margin-left: 5px;
          }
        }

        .chart-score {
          display: inline-block;
          width: 32px;
          height: 32px;
          border-radius: 50%;
          background-color: #fff;

          & + .chart-score {
            margin-left: 5px;
          }
        }
      }
    }
  }

  .fixed-list-navigation {
    position: fixed;
    bottom: 48px;
    right: 124px;

    .navigation-btn {
      width: 40px;
      height: 40px;
      padding: 0 8px;
      border-radius: 50%;
      border: 0;
      box-shadow: 0px 2px 4px 0px rgba(0,0,0,0.06), 0px 3px 6px 0px rgba(0,0,1,0.12);
      text-align: center;
      background: #ffffff;

      & + .navigation-btn {
        margin-left: 38px;
      }

      &:not(.is-disabled):hover {
        background: #0096cc;

        .btn-icon {
          color: #ffffff;
        }
      }

      &.prev-btn {
        .btn-icon {
          transform: rotateZ(180deg);
        }
      }

      .btn-icon {
        display: inline-block;
        font-size: 15px;
        color: #606266;

        &::before {
          padding-left: 2px;
        }
      }
    }
  }
}
</style>
